import React, { useState, useEffect, useContext } from "react";
import secureAxios from "../../utils/secureAxios";
import { getApiBaseURL } from "../../utils/api";
import { formatPhone } from "../../utils/formatPhone";
import { AuthContext } from "../../contexts/AuthContext";

const CreateConsumerForm = ({ onSuccess }) => {
  const { user } = useContext(AuthContext);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone_number: "",
    counselor_id: "",
    status_id: "",
    referred_by: "",
    case_id: "",
    date_of_birth: "",
    notes: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state_id: "",
    postal_code: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    hipaa_consent: false,
    data_sharing_consent: false,
    // Demographics
    age: "",
    sex: "",
    marital_status: "",
    // Employment Assessment Fields (CRP-31)
    family_situation: "",
    education: "",
    transportation: "",
    work_history: "",
    positive_work_traits: "",
    negative_work_traits: "",
    job_preference: "",
    salary_needs: "",
    benefits_needed: "",
    restrictions: "",
  });

  const [counselors, setCounselors] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [showEmploymentFields, setShowEmploymentFields] = useState(false);

  const apiBase = getApiBaseURL();

  useEffect(() => {
    if (!user?.tenant_id) return;

    const fetchData = async () => {
      try {
        const [counselorsRes, statusesRes, statesRes] = await Promise.all([
          secureAxios.get(`${apiBase}/api/counselors`, {
            params: { tenant_id: user.tenant_id },
          }),
          secureAxios.get(`${apiBase}/api/status`, {
            params: { tenant_id: user.tenant_id },
          }),
          secureAxios.get(`${apiBase}/api/states`),
        ]);

        setCounselors(counselorsRes.data);

        // âœ… Filter to only show "Account Type" statuses
        const accountTypeStatuses = statusesRes.data.filter(
          (status) => status.status_type_name === "Account Type"
        );
        setStatuses(accountTypeStatuses);

        setStates(statesRes.data);
      } catch (err) {
        console.error("Failed to fetch form data:", err);
        setError("Failed to load form data. Please refresh the page.");
      }
    };

    fetchData();
  }, [user?.tenant_id, apiBase]);

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return "";
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age >= 0 ? age.toString() : "";
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = value;

    if (name === "phone_number" || name === "emergency_contact_phone") {
      newValue = formatPhone(value);
    }

    // Auto-calculate age when date of birth changes
    if (name === "date_of_birth") {
      const calculatedAge = calculateAge(value);
      setFormData((prev) => ({
        ...prev,
        date_of_birth: value,
        age: calculatedAge,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : newValue,
      }));
    }

    // Clear error on change
    if (error) setError("");
    if (message) setMessage("");
  };

  const validateForm = () => {
    // Required fields validation
    if (!formData.name.trim()) {
      setError("Consumer name is required");
      return false;
    }

    if (!formData.counselor_id) {
      setError("Counselor selection is required");
      return false;
    }

    if (!formData.status_id) {
      setError("Status selection is required");
      return false;
    }

    // Email validation if provided
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError("Please enter a valid email address");
      return false;
    }

    // Date of birth validation (HIPAA requires special handling)
    if (formData.date_of_birth) {
      const dob = new Date(formData.date_of_birth);
      const today = new Date();
      if (dob > today) {
        setError("Date of birth cannot be in the future");
        return false;
      }
    }

    // HIPAA consent required
    if (!formData.hipaa_consent) {
      setError("HIPAA consent is required to create a consumer record");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    const payload = {
      ...formData,
      phone_number: formData.phone_number.replace(/\D/g, ""),
      emergency_contact_phone: formData.emergency_contact_phone.replace(
        /\D/g,
        ""
      ),
      created_by: user?.user_id,
      tenant_id: user?.tenant_id,
    };

    try {
      await secureAxios.post(`${apiBase}/api/consumers`, payload);

      setMessage("âœ… Consumer created successfully!");

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone_number: "",
        counselor_id: "",
        status_id: "",
        referred_by: "",
        case_id: "",
        date_of_birth: "",
        notes: "",
        address_line_1: "",
        address_line_2: "",
        city: "",
        state_id: "",
        postal_code: "",
        emergency_contact_name: "",
        emergency_contact_phone: "",
        hipaa_consent: false,
        data_sharing_consent: false,
        age: "",
        sex: "",
        marital_status: "",
        family_situation: "",
        education: "",
        transportation: "",
        work_history: "",
        positive_work_traits: "",
        negative_work_traits: "",
        job_preference: "",
        salary_needs: "",
        benefits_needed: "",
        restrictions: "",
      });

      if (onSuccess) {
        setTimeout(() => onSuccess(), 1500);
      }
    } catch (err) {
      console.error("âŒ Error creating consumer:", err);
      setError(
        err.response?.data?.error ||
          "Failed to create consumer. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Create Consumer</h2>
        <span className="badge bg-warning text-dark">
          <i className="bi bi-shield-lock"></i> PHI Entry Form
        </span>
      </div>

      {/* PHI Warning */}
      <div className="alert alert-info mb-3" role="alert">
        <i className="bi bi-shield-exclamation"></i>{" "}
        <strong>Protected Health Information Notice:</strong>
        <br />
        You are about to enter Protected Health Information (PHI). This data is
        subject to HIPAA regulations. All entries will be encrypted and access
        will be logged for compliance.
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError("")}
            aria-label="Close"
          ></button>
        </div>
      )}

      {message && (
        <div className="alert alert-success alert-dismissible" role="alert">
          {message}
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="row g-3">
        {/* Basic Information */}
        <div className="col-12">
          <h5 className="border-bottom pb-2">Basic Information</h5>
        </div>

        <div className="col-md-4">
          <label className="form-label">
            Name <span className="text-danger">*</span>
          </label>
          <input
            className="form-control"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Consumer full name"
            required
            disabled={loading}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">Email</label>
          <input
            className="form-control"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="email@example.com"
            disabled={loading}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">Phone Number</label>
          <input
            className="form-control"
            type="tel"
            name="phone_number"
            value={formData.phone_number}
            onChange={handleChange}
            placeholder="(123) 456-7890"
            disabled={loading}
          />
        </div>

        {/* Demographics Row */}
        <div className="col-md-2">
          <label className="form-label">Sex</label>
          <select
            className="form-select"
            name="sex"
            value={formData.sex}
            onChange={handleChange}
            disabled={loading}
          >
            <option value="">Select</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="col-md-2">
          <label className="form-label">Marital Status</label>
          <select
            className="form-select"
            name="marital_status"
            value={formData.marital_status}
            onChange={handleChange}
            disabled={loading}
          >
            <option value="">Select</option>
            <option value="Single">Single</option>
            <option value="Married">Married</option>
            <option value="Divorced">Divorced</option>
            <option value="Widowed">Widowed</option>
            <option value="Separated">Separated</option>
          </select>
        </div>

        <div className="col-md-3">
          <label className="form-label">
            Date of Birth
            <i className="bi bi-shield-lock text-warning ms-2" title="PHI"></i>
          </label>
          <input
            className="form-control"
            type="date"
            name="date_of_birth"
            value={formData.date_of_birth}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        <div className="col-md-2">
          <label className="form-label">Age</label>
          <input
            className="form-control"
            type="text"
            name="age"
            value={formData.age}
            onChange={handleChange}
            placeholder="—"
            disabled={loading || !!formData.date_of_birth}
            readOnly={!!formData.date_of_birth}
            title={
              formData.date_of_birth
                ? "Age is calculated from Date of Birth"
                : "Enter age manually or provide Date of Birth"
            }
          />
          {formData.date_of_birth && (
            <small className="text-muted">Auto-calculated</small>
          )}
        </div>

        <div className="col-md-3">
          <label className="form-label">Case ID</label>
          <input
            className="form-control"
            name="case_id"
            value={formData.case_id}
            onChange={handleChange}
            placeholder="Case identifier"
            disabled={loading}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">
            Status <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            name="status_id"
            value={formData.status_id}
            onChange={handleChange}
            required
            disabled={loading}
          >
            <option value="">Select Status</option>
            {statuses.map((s) => (
              <option key={s.status_id} value={s.status_id}>
                {s.status}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <label className="form-label">
            Counselor <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            name="counselor_id"
            value={formData.counselor_id}
            onChange={handleChange}
            required
            disabled={loading}
          >
            <option value="">Select Counselor</option>
            {counselors.map((c) => (
              <option key={c.counselor_id} value={c.counselor_id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <label className="form-label">Referred By</label>
          <input
            className="form-control"
            name="referred_by"
            value={formData.referred_by}
            onChange={handleChange}
            placeholder="Referral source"
            disabled={loading}
          />
        </div>

        {/* Address Section */}
        <div className="col-12">
          <h5 className="border-bottom pb-2 mt-3">Address Information</h5>
        </div>

        <div className="col-12">
          <label className="form-label">Address Line 1</label>
          <input
            className="form-control"
            name="address_line_1"
            value={formData.address_line_1}
            onChange={handleChange}
            placeholder="Street address"
            disabled={loading}
          />
        </div>

        <div className="col-12">
          <label className="form-label">Address Line 2</label>
          <input
            className="form-control"
            name="address_line_2"
            value={formData.address_line_2}
            onChange={handleChange}
            placeholder="Apartment, suite, etc."
            disabled={loading}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">City</label>
          <input
            className="form-control"
            name="city"
            value={formData.city}
            onChange={handleChange}
            placeholder="City"
            disabled={loading}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">State</label>
          <select
            className="form-select"
            name="state_id"
            value={formData.state_id}
            onChange={handleChange}
            disabled={loading}
          >
            <option value="">Select State</option>
            {states.map((s) => (
              <option key={s.state_id} value={s.state_id}>
                {s.state_code} - {s.state_name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <label className="form-label">ZIP Code</label>
          <input
            className="form-control"
            name="postal_code"
            value={formData.postal_code}
            onChange={handleChange}
            placeholder="12345"
            maxLength="10"
            disabled={loading}
          />
        </div>

        {/* Emergency Contact */}
        <div className="col-12">
          <h5 className="border-bottom pb-2 mt-3">Emergency Contact</h5>
        </div>

        <div className="col-md-6">
          <label className="form-label">Emergency Contact Name</label>
          <input
            className="form-control"
            name="emergency_contact_name"
            value={formData.emergency_contact_name}
            onChange={handleChange}
            placeholder="Contact name"
            disabled={loading}
          />
        </div>

        <div className="col-md-6">
          <label className="form-label">Emergency Contact Phone</label>
          <input
            className="form-control"
            type="tel"
            name="emergency_contact_phone"
            value={formData.emergency_contact_phone}
            onChange={handleChange}
            placeholder="(123) 456-7890"
            disabled={loading}
          />
        </div>

        {/* Employment Assessment Data (Collapsible) */}
        <div className="col-12">
          <div className="d-flex align-items-center mt-3 border-bottom pb-2">
            <h5 className="mb-0 me-2">Employment Assessment Data</h5>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setShowEmploymentFields(!showEmploymentFields)}
            >
              {showEmploymentFields ? (
                <>
                  <i className="bi bi-chevron-up me-1"></i>Hide
                </>
              ) : (
                <>
                  <i className="bi bi-chevron-down me-1"></i>Show
                </>
              )}
            </button>
            <small className="text-muted ms-2">
              (Optional - can be filled via CRP-31 form)
            </small>
          </div>
        </div>

        {showEmploymentFields && (
          <>
            <div className="col-md-12">
              <label className="form-label">Family Situation</label>
              <textarea
                className="form-control"
                name="family_situation"
                value={formData.family_situation}
                onChange={handleChange}
                rows="2"
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Education</label>
              <textarea
                className="form-control"
                name="education"
                value={formData.education}
                onChange={handleChange}
                rows="2"
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Transportation</label>
              <textarea
                className="form-control"
                name="transportation"
                value={formData.transportation}
                onChange={handleChange}
                rows="2"
                disabled={loading}
              />
            </div>

            <div className="col-md-12">
              <label className="form-label">Restrictions (if any)</label>
              <textarea
                className="form-control"
                name="restrictions"
                value={formData.restrictions}
                onChange={handleChange}
                rows="2"
                disabled={loading}
              />
            </div>

            <div className="col-md-12">
              <label className="form-label">Work History</label>
              <textarea
                className="form-control"
                name="work_history"
                value={formData.work_history}
                onChange={handleChange}
                rows="3"
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Positive Work Traits</label>
              <textarea
                className="form-control"
                name="positive_work_traits"
                value={formData.positive_work_traits}
                onChange={handleChange}
                rows="2"
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Negative Work Traits</label>
              <textarea
                className="form-control"
                name="negative_work_traits"
                value={formData.negative_work_traits}
                onChange={handleChange}
                rows="2"
                disabled={loading}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Job Preference</label>
              <input
                className="form-control"
                name="job_preference"
                value={formData.job_preference}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Salary Needs</label>
              <input
                className="form-control"
                name="salary_needs"
                value={formData.salary_needs}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Benefits Needed</label>
              <input
                className="form-control"
                name="benefits_needed"
                value={formData.benefits_needed}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
          </>
        )}

        {/* Notes */}
        <div className="col-12">
          <label className="form-label">
            Notes
            <i className="bi bi-shield-lock text-warning ms-2" title="PHI"></i>
          </label>
          <textarea
            className="form-control"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows="3"
            placeholder="Enter any relevant notes (may contain PHI)"
            disabled={loading}
          />
          <small className="text-muted">
            Notes may contain Protected Health Information and will be
            encrypted.
          </small>
        </div>

        {/* Consent Section */}
        <div className="col-12">
          <h5 className="border-bottom pb-2 mt-3">Required Consents</h5>
        </div>

        <div className="col-12">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              name="hipaa_consent"
              id="hipaa_consent"
              checked={formData.hipaa_consent}
              onChange={handleChange}
              disabled={loading}
              required
            />
            <label className="form-check-label" htmlFor="hipaa_consent">
              <strong>
                HIPAA Consent <span className="text-danger">*</span>
              </strong>
              <br />I acknowledge that I have obtained appropriate consent to
              enter this consumer's Protected Health Information (PHI) into the
              system.
            </label>
          </div>
        </div>

        <div className="col-12">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              name="data_sharing_consent"
              id="data_sharing_consent"
              checked={formData.data_sharing_consent}
              onChange={handleChange}
              disabled={loading}
            />
            <label className="form-check-label" htmlFor="data_sharing_consent">
              <strong>Data Sharing Consent</strong>
              <br />
              Consumer has consented to share their information with authorized
              care team members.
            </label>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="col-12">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !formData.hipaa_consent}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Creating...
              </>
            ) : (
              <>
                <i className="bi bi-person-plus"></i> Create Consumer
              </>
            )}
          </button>
          <button
            type="button"
            className="btn btn-secondary ms-2"
            onClick={() => {
              setFormData({
                name: "",
                email: "",
                phone_number: "",
                counselor_id: "",
                status_id: "",
                referred_by: "",
                case_id: "",
                date_of_birth: "",
                notes: "",
                address_line_1: "",
                address_line_2: "",
                city: "",
                state_id: "",
                postal_code: "",
                emergency_contact_name: "",
                emergency_contact_phone: "",
                hipaa_consent: false,
                data_sharing_consent: false,
                age: "",
                sex: "",
                marital_status: "",
                family_situation: "",
                education: "",
                transportation: "",
                work_history: "",
                positive_work_traits: "",
                negative_work_traits: "",
                job_preference: "",
                salary_needs: "",
                benefits_needed: "",
                restrictions: "",
              });
              setError("");
              setMessage("");
            }}
            disabled={loading}
          >
            Clear Form
          </button>
        </div>
      </form>

      {/* HIPAA Compliance Notice */}
      <div className="alert alert-secondary mt-3" role="alert">
        <h6>
          <i className="bi bi-shield-check"></i> HIPAA Compliance Information
        </h6>
        <small>
          <ul className="mb-0">
            <li>
              All consumer data is encrypted at rest (AES-256) and in transit
              (TLS 1.3)
            </li>
            <li>
              Access to consumer records is logged with user, timestamp, and
              purpose
            </li>
            <li>Audit logs are retained for 7 years per HIPAA requirements</li>
            <li>Date of birth and case notes are flagged as sensitive PHI</li>
            <li>
              Emergency contacts are maintained for break-glass access scenarios
            </li>
            <li>
              Only authorized personnel with valid business need can access
              consumer data
            </li>
          </ul>
        </small>
      </div>
    </div>
  );
};

export default CreateConsumerForm;
